import {
  recommendedSubscriptionAction,
  type SubscriptionAction,
} from '@otter-ai/recall-client';
import { MINUTE_MS } from '@/constants/time';
import type { AdditionalQueryOptions } from '@/shared/utils/queryCreators';

export const recommendedSubscriptionActionQueryKey = [
  'recommended_subscription_action',
] as const;
type RecommendedSubscriptionActionQueryKey =
  typeof recommendedSubscriptionActionQueryKey;

const staleTime = 15 * MINUTE_MS; // Data stays fresh for 15 minutes

type AdditionalRecommendedSubscriptionActionQueryOptions =
  AdditionalQueryOptions<
    { action: SubscriptionAction },
    RecommendedSubscriptionActionQueryKey
  >;

export type RecommendedSubscriptionActionQuery = {
  readonly queryKey: RecommendedSubscriptionActionQueryKey;
  readonly queryFn: () => Promise<{ action: SubscriptionAction }>;
} & AdditionalRecommendedSubscriptionActionQueryOptions;

/**
 * Create a query to fetch the recommended subscription action for the user.
 * Returns 'cc_trial' if eligible for CC trials, 'direct_purchase' otherwise.
 *
 * @example
 * ```tsx
 * const { data } = useQuery(recommendedSubscriptionActionQueryCreator(workspaceId ?? 0));
 * const isCCTrialEligible = data?.recommended_action === 'cc_trial';
 * ```
 */
export function recommendedSubscriptionActionQueryCreator(
  workspaceId: number,
  additionalOptions: AdditionalRecommendedSubscriptionActionQueryOptions = {},
): RecommendedSubscriptionActionQuery {
  return {
    staleTime,
    queryKey: recommendedSubscriptionActionQueryKey,
    queryFn: async () => {
      const response = await recommendedSubscriptionAction({
        query: { workspace_id: workspaceId },
        throwOnError: true,
      });
      return response.data.data;
    },
    enabled: !!workspaceId,
    ...additionalOptions,
  };
}
